/**
 * The web app's locales (`apps/admin` stays English).
 *
 * LEAF MODULE: `proxy.ts` and the `app/[locale]` root layout import it, so it
 * holds plain data and pure helpers only.
 *
 * A locale is its URL slug, always lowercase (`pt-br`); `toLanguageTag`
 * gives the BCP 47 tag for `<html lang>`, hreflang and `Intl`.
 */
export const LOCALES = [
  'en',
  'de',
  'fr',
  'es',
  'pt-br',
  'it',
  'nl',
  'pl',
  'sv',
  'cs',
  'tr',
  'ja',
  'ko',
  'th',
  'vi',
  'id',
] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

/**
 * The locales the app serves: every one of them. A new locale joins
 * `LOCALES` in the PR that ships every reviewed catalog for it
 * (`pnpm i18n:check` holds an enabled locale to a complete catalog).
 */
export const ENABLED_LOCALES: ReadonlyArray<Locale> = LOCALES

/** Remembers a visitor's explicit language choice (switcher, settings). */
export const LOCALE_COOKIE = 'NEXT_LOCALE'

/**
 * The request header the proxy sets to a prefixed URL's locale (and removes
 * from every other request): `app/global-not-found.tsx`, which has no params,
 * answers an unknown `/de/…` URL in German with it.
 */
export const URL_LOCALE_HEADER = 'x-url-locale'

/**
 * The query parameter a language switcher adds to the page it opens
 * (`?set_locale=de`): the proxy stores the choice and redirects to the
 * clean URL, so a visitor who never chose stays on the URL's language.
 */
export const SET_LOCALE_PARAM = 'set_locale'

export const LOCALE_COOKIE_OPTIONS = {
  path: '/',
  maxAge: 60 * 60 * 24 * 365,
  sameSite: 'lax',
  secure: true,
} as const

const LANGUAGE_TAGS: Readonly<Record<Locale, string>> = {
  en: 'en',
  de: 'de',
  fr: 'fr',
  es: 'es',
  'pt-br': 'pt-BR',
  it: 'it',
  nl: 'nl',
  pl: 'pl',
  sv: 'sv',
  cs: 'cs',
  tr: 'tr',
  ja: 'ja',
  ko: 'ko',
  th: 'th',
  vi: 'vi',
  id: 'id',
}

/**
 * Each locale's name in its own language, for language pickers: a reader
 * finds their language by the name they know it by.
 */
export const LOCALE_NATIVE_NAMES: Readonly<Record<Locale, string>> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  'pt-br': 'Português (Brasil)',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  sv: 'Svenska',
  cs: 'Čeština',
  tr: 'Türkçe',
  ja: '日本語',
  ko: '한국어',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
}

const KNOWN_LOCALES: ReadonlySet<string> = new Set(LOCALES)

export function isLocale(value: string): value is Locale {
  return KNOWN_LOCALES.has(value)
}

export function isEnabledLocale(
  value: string | null | undefined,
  enabledLocales: ReadonlyArray<Locale> = ENABLED_LOCALES
): value is Locale {
  return (
    typeof value === 'string' &&
    isLocale(value) &&
    enabledLocales.includes(value)
  )
}

export function toLanguageTag(locale: Locale): string {
  return LANGUAGE_TAGS[locale]
}

/** The locale whose `toLanguageTag` is `tag` (`pt-BR` → `pt-br`), if any. */
export function localeFromLanguageTag(tag: string): Locale | null {
  const value = tag.toLowerCase()
  return isLocale(value) ? value : null
}

/**
 * `pathname` split into its leading enabled locale segment and the rest:
 * `/de/pricing` → `de` + `/pricing`; `/pricing` → `null` + `/pricing`.
 */
export function splitLocalePrefix(
  pathname: string,
  enabledLocales: ReadonlyArray<Locale> = ENABLED_LOCALES
): { locale: Locale | null; path: string } {
  const first = pathname.split('/')[1]
  if (!isEnabledLocale(first, enabledLocales)) {
    return { locale: null, path: pathname }
  }
  return { locale: first, path: pathname.slice(first.length + 1) || '/' }
}
